import React, { useEffect, useState } from 'react';
import { func, bool } from 'prop-types';

import useUserStore from 'stores/user';
import usePurchaseStore from 'stores/purchase';
import useCurrentGameStore from 'stores/current-game';
import useUpcomingGameStore from 'stores/upcoming-game';
import useApiErrorStore from 'stores/api-error';
import { shallow } from 'zustand/shallow';
import api from 'utils/api';
import { text } from 'polyglot/polyglot';
import { truncate } from 'lodash';
import Modal from 'components/Modal/Modal';
import Button from 'components/Button/Button';
import ConfirmPurchase from './ConfirmPurchase/ConfirmPurchase';
import PurchaseReceipt from './PurchaseReceipt/PurchaseReceipt';
import IncuffientFunds from './IncuffientFunds/IncuffientFunds';

import styles from './IncuffientFunds/IncuffientFunds.module.scss';

const CONFIRM_PURCHASE = 'CONFIRM_PURCHASE';
const PURCHASE_RECEIPT = 'PURCHASE_RECEIPT';
const INCUFFIENT_FUNDS = 'INCUFFIENT_FUNDS';
const NO_GAME_LIMITS = 'NO_GAME_LIMITS';

const selectedCardIdSelector = (state) => [
  state.selectedCardIds,
  state.resetSelectedCardIds,
];
const bingoCardsListSelector = (state) => [
  state.bingoCardsList,
  state.setBingoCardsList,
];
const userSelector = (state) => [state.user, state.setUser];
const currentGameSelector = (state) => state.currentGame;
const prebuyTicketAmountSelector = (state) => [
  state.prebuyTicketAmount,
  state.updatePrebuyTicketAmount,
];
const upcomingRoundIdSelector = (state) => [
  state.upcomingRoundId,
  state.setUpcomingRoundId,
];
const upcomingGameSelector = (state) => state.nextGame;
const setApiError = (state) => state.setErrorMessage;
const updateTicketsCurrentGameSelector = (state) => [
  state.numberOfTicketsCurrentGame,
  state.updateTicketsCurrentGame,
];
const updateTicketsNextGameSelector = (state) => [
  state.numberOfTicketsNextGame,
  state.updateTicketsNextGame,
];
const setFetchNewCardsSelector = (state) => state.setFetchNewCards;

function PurchaseModal({ onClose, isPrebuy }) {
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState(CONFIRM_PURCHASE);
  const [selectedCardIds, resetSelectedCardIds] = usePurchaseStore(
    selectedCardIdSelector,
    shallow
  );
  const [bingoCardsList, setBingoCardsList] = usePurchaseStore(
    bingoCardsListSelector,
    shallow
  );
  const currentGame = useCurrentGameStore(currentGameSelector);
  const nextGame = useUpcomingGameStore(upcomingGameSelector);
  const [prebuyTicketAmount, setPrebuyTicketAmount] = usePurchaseStore(
    prebuyTicketAmountSelector,
    shallow
  );
  const [upcomingRoundId, setUpcomingRoundId] = usePurchaseStore(
    upcomingRoundIdSelector,
    shallow
  );
  // eslint-disable-next-line prettier/prettier
  const [
    numberOfTicketsCurrentGame,
    updateTicketsCurrentGame,
    // eslint-disable-next-line prettier/prettier
  ] = usePurchaseStore(updateTicketsCurrentGameSelector);
  const [numberOfTicketsNextGame, updateTicketsNextGame] = usePurchaseStore(
    updateTicketsNextGameSelector,
    shallow
  );
  const cardPrice = isPrebuy ? nextGame.cardPrice : currentGame.cardPrice;
  const cardsCount = isPrebuy ? prebuyTicketAmount : selectedCardIds.length;
  const bingoGameId = isPrebuy ? nextGame.bingoGameId : currentGame.bingoGameId;
  const totalPrice = cardPrice * cardsCount;
  const [user, setUser] = useUserStore(userSelector, shallow);
  const setApiErrorMessage = useApiErrorStore(setApiError);
  const setFetchNewCards = usePurchaseStore(setFetchNewCardsSelector);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [sumNotIncludedInBonus, setSumNotIncludedInBonus] = useState(null);

  const checkIfUserHasActiveBonus = () => {
    let hasBonus = null;
    if (user?.customerBonus) {
      user.customerBonus.forEach((bonus) => {
        if (hasBonus) return;
        const { qualificationType, gameId, baseGameType } = bonus.qualification;
        if (qualificationType !== 'TurnoverProducts') return;
        if (!baseGameType && !gameId) {
          hasBonus = bonus;
        }
        if (
          isPrebuy &&
          gameId === nextGame.bingoGameId &&
          baseGameType === 'Bingo'
        ) {
          hasBonus = bonus;
        }
        if (
          !isPrebuy &&
          gameId === currentGame.bingoGameId &&
          baseGameType === 'Bingo'
        ) {
          hasBonus = bonus;
        }
      });
    }
    return hasBonus;
  };
  const displayBonus = checkIfUserHasActiveBonus();

  const updateCustomerBonus = async () => {
    await api
      .get('/api/CustomerBonus')
      .then((res) => {
        setUser({
          customerBonus: res,
        });
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log('error on getting bonus information: ', err);
      });
  };

  useEffect(() => {
    updateCustomerBonus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickClose = () => {
    if (!isLoading) {
      if (currentView === PURCHASE_RECEIPT) {
        if (isPrebuy) {
          setPrebuyTicketAmount(0);
          setUpcomingRoundId(null);
        } else {
          resetSelectedCardIds();
          setBingoCardsList([]);
          setFetchNewCards(true);
        }
      }
      // setCurrentView(CONFIRM_PURCHASE);

      onClose();
    }
  };

  const checkErrorMessage = (err) => {
    switch (err.data) {
      case 'Not_Enough_Funds_To_Pay':
        setCurrentView(INCUFFIENT_FUNDS);
        break;
      case 'Deposit_Limit_Not_Set':
        setCurrentView(NO_GAME_LIMITS);
        break;
      case 'Has_Exceeded_Loss_Limit':
        onClose();
        setApiErrorMessage(13);
        break;
      case 'Has_Exceeded_Time_Limit':
        onClose();
        setApiErrorMessage(14);
        break;
      case 'Has_Game_Stop':
        onClose();
        setApiErrorMessage(15);
        break;
      case 'Customer_Bingo_Blocked':
        onClose();
        setApiErrorMessage(16);
        break;
      case 'BingoRound_Is_Closed_For_Purchasing_Cards':
        onClose();
        setApiErrorMessage(17);
        break;
      default:
        onClose();
        setApiErrorMessage(10);
        break;
    }
  };

  const onClickConfirm = async () => {
    if (displayBonus) {
      setSelectedBonus({ ...displayBonus });
    }
    setIsLoading(true);

    if (isPrebuy) {
      const upcomingBingoRoundId = upcomingRoundId || nextGame.bingoRoundId;
      const data = {
        bingoRoundId: upcomingBingoRoundId,
        numberOfCards: cardsCount,
      };
      await api
        .post('/api/Bingo/prebuy', data)
        .then(() => {
          if (
            user.callback_purchase &&
            typeof user.callback_purchase === 'function'
          ) {
            user.callback_purchase({
              gameId: String(nextGame.bingoGameId),
              name: nextGame.name,
              ticketPrice: cardPrice,
              quantity: cardsCount,
            });
          }
          if (displayBonus) {
            updateCustomerBonus();
          }
          updateTicketsNextGame(numberOfTicketsNextGame + cardsCount);
          setUser({ updateBalance: true });
          setIsLoading(false);
          setCurrentView(PURCHASE_RECEIPT);
        })
        .catch((err) => {
          setIsLoading(false);
          checkErrorMessage(err);
        });
    } else {
      const cardsToPurchase = [];
      await selectedCardIds.forEach((cardId) => {
        cardsToPurchase.push(
          bingoCardsList.find((element) => element.checksum === cardId)
        );
      });

      const data = {
        bingoRoundId: currentGame.bingoRoundId,
        cardsToBuy: cardsToPurchase,
      };

      await api
        .post('/api/Bingo', data)
        .then(() => {
          if (
            user.callback_purchase &&
            typeof user.callback_purchase === 'function'
          ) {
            user.callback_purchase({
              gameId: String(currentGame.bingoGameId),
              name: currentGame.name,
              ticketPrice: cardPrice,
              quantity: cardsCount,
            });
          }
          if (displayBonus) {
            updateCustomerBonus();
          }
          updateTicketsCurrentGame(numberOfTicketsCurrentGame + cardsCount);
          setUser({ updateBalance: true });
          setIsLoading(false);
          setCurrentView(PURCHASE_RECEIPT);
        })
        .catch((err) => {
          setIsLoading(false);
          checkErrorMessage(err);
        });
    }
  };

  const onClickLoadMoney = () => {
    if (user.callback_deposit && typeof user.callback_deposit === 'function') {
      user.callback_deposit();
    }
    onClickClose();
  };

  switch (currentView) {
    case PURCHASE_RECEIPT:
      return (
        <PurchaseReceipt
          onClose={onClickClose}
          isLoading={isLoading}
          cardPrice={cardPrice}
          cardsCount={cardsCount}
          totalPrice={totalPrice}
          username={truncate(user.nickname, { length: 16 })}
          bingoName={isPrebuy ? nextGame.name : currentGame.name}
          displayBonus={selectedBonus}
          sumNotIncludedInBonus={sumNotIncludedInBonus}
        />
      );

    case INCUFFIENT_FUNDS:
      return (
        <IncuffientFunds
          onClose={onClickClose}
          onClickLoadMoney={onClickLoadMoney}
          isLoading={isLoading}
        />
      );

    case NO_GAME_LIMITS:
      return (
        <Modal
          isLoading={isLoading}
          onClickOutside={onClose}
          title={text.t('purchaseModal.noGameLimitsSet.title')}
          content={
            <div className={styles.text}>
              <p>{text.t('purchaseModal.noGameLimitsSet.text')}</p>
            </div>
          }
          buttons={[
            <Button
              key="confirm-button"
              className={styles.button}
              onClick={onClickClose}
              isTertiary
            >
              {text.t('purchaseModal.noGameLimitsSet.closeModalButtonText')}
            </Button>,
          ]}
        />
      );

    default:
      return (
        <ConfirmPurchase
          onClose={onClickClose}
          onClickConfirm={onClickConfirm}
          isLoading={isLoading}
          cardPrice={cardPrice}
          cardsCount={cardsCount}
          totalPrice={totalPrice}
          bingoName={isPrebuy ? nextGame.name : currentGame.name}
          displayBonus={displayBonus}
          bingoGameId={bingoGameId}
          sumNotIncludedInBonus={sumNotIncludedInBonus}
          setSumNotIncludedInBonus={setSumNotIncludedInBonus}
        />
      );
  }
}

PurchaseModal.propTypes = {
  onClose: func,
  isPrebuy: bool.isRequired,
};

PurchaseModal.defaultProps = {
  onClose: () => {},
};

export default PurchaseModal;
